import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, NavLink, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import axios from 'axios';
import { LayoutDashboard, TestTube2, LineChart, Settings, TrendingUp, LogOut, Zap } from 'lucide-react';
import Dashboard from './pages/Dashboard';
import Backtesting from './pages/Backtesting';
import PaperTrading from './pages/PaperTrading';
import AutoTrading from './pages/AutoTrading';
import SettingsPage from './pages/Settings';
import Login from './pages/Login';
import './App.css';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Configure axios interceptor to add auth token
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 responses globally
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_expires');
      window.location.reload();
    }
    return Promise.reject(error);
  }
);

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('auth_token');
    const expires = localStorage.getItem('auth_expires');

    if (!token || !expires) {
      setIsAuthenticated(false);
      setCheckingAuth(false);
      return;
    }

    // Check if token is expired locally
    if (new Date(expires) < new Date()) {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_expires');
      setIsAuthenticated(false);
      setCheckingAuth(false);
      return;
    }

    // Verify token with server
    try {
      await axios.get(`${API_URL}/api/auth/verify`);
      setIsAuthenticated(true);
    } catch (error) {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_expires');
      setIsAuthenticated(false);
    } finally {
      setCheckingAuth(false);
    }
  };

  const handleLogin = (token) => {
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_expires');
    setIsAuthenticated(false);
  };

  if (checkingAuth) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#0B0D0F'
      }}>
        <div className="loading-spinner" style={{ width: '40px', height: '40px' }} />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <>
        <Login onLogin={handleLogin} />
        <Toaster 
          position="top-right" 
          theme="dark"
          toastOptions={{
            style: {
              background: '#101317',
              border: '1px solid #1E2329',
              color: '#E6E8EC'
            }
          }}
        />
      </>
    );
  }

  return (
    <Router>
      <div className="app-container">
        {/* Sidebar */}
        <aside className="sidebar">
          <div className="sidebar-header">
            <div className="sidebar-logo">
              <div className="sidebar-logo-icon">
                <TrendingUp size={24} color="#0B0D0F" />
              </div>
              <h1>CryptoAlgo</h1>
            </div>
          </div>
          
          <nav className="sidebar-nav">
            <NavLink 
              to="/" 
              className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
              data-testid="nav-dashboard"
              end
            >
              <LayoutDashboard size={20} />
              <span>Dashboard</span>
            </NavLink>
            
            <NavLink 
              to="/backtesting" 
              className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
              data-testid="nav-backtesting"
            >
              <TestTube2 size={20} />
              <span>Backtesting</span>
            </NavLink>
            
            <NavLink 
              to="/paper-trading" 
              className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
              data-testid="nav-paper-trading"
            >
              <LineChart size={20} />
              <span>Paper Trading</span>
            </NavLink>

            <NavLink 
              to="/auto-trading" 
              className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
              data-testid="nav-auto-trading"
              style={{ borderLeft: '2px solid #F59E0B' }}
            >
              <Zap size={20} color="#F59E0B" />
              <span style={{ color: '#F59E0B' }}>Auto Trading</span>
            </NavLink>
            
            <NavLink 
              to="/settings" 
              className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
              data-testid="nav-settings"
            >
              <Settings size={20} />
              <span>Settings</span>
            </NavLink>

            <div style={{ flex: 1 }} />

            <button 
              onClick={handleLogout}
              className="nav-item"
              style={{ 
                width: '100%', 
                border: 'none', 
                cursor: 'pointer',
                textAlign: 'left'
              }}
              data-testid="logout-button"
            >
              <LogOut size={20} />
              <span>Cerrar Sesión</span>
            </button>
          </nav>
        </aside>

        {/* Main Content */}
        <main className="main-content">
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/backtesting" element={<Backtesting />} />
            <Route path="/paper-trading" element={<PaperTrading />} />
            <Route path="/auto-trading" element={<AutoTrading />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>

        <Toaster 
          position="top-right" 
          theme="dark"
          toastOptions={{
            style: {
              background: '#101317',
              border: '1px solid #1E2329',
              color: '#E6E8EC'
            }
          }}
        />
      </div>
    </Router>
  );
}

export default App;
